import React, { useEffect, useRef, useState } from 'react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import Seo from '../Seo';
import '../features/features.css';
import './legal.css';

// How far below the top of the screen a heading must pass before its contents
// item becomes active: the sticky navbar plus a little breathing room.
const ACTIVE_OFFSET = 120;

const slugify = (text) =>
    text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

/** Orange band under the navbar, shared by Compliance, Security and the legal pages. */
export function LegalBand({ title, sub }) {
    return (
        <header className="bg-primary-theme wql-band">
            <div className="wqf-wrap">
                <h1 className="wqf-h1 wql-title">{title}</h1>
                {sub && <p className="wql-sub">{sub}</p>}
            </div>
        </header>
    );
}

/** One titled section of a legal document. Its heading also appears in the contents. */
export function LegalSection({ id, title, children }) {
    return (
        <section id={id || slugify(title)} className="wql-section">
            <h2 className="wql-h2">{title}</h2>
            {children}
        </section>
    );
}

export function LegalList({ items, ordered = false }) {
    const List = ordered ? 'ol' : 'ul';
    return (
        <List className="wql-list">
            {items.map((item, i) => (
                <li key={i}>{item}</li>
            ))}
        </List>
    );
}

/**
 * Frame for the Privacy, Cookies and Terms pages: navbar, band, a sticky
 * contents card (desktop only) and the document card.
 */
function LegalPage({ seo, title, children }) {
    const docRef = useRef(null);
    const [contents, setContents] = useState([]);
    const [activeId, setActiveId] = useState(null);

    useEffect(() => {
        const sections = Array.from(docRef.current.querySelectorAll('.wql-section'));
        if (!sections.length) return undefined;

        // Built from the headings on the page, so the wording only lives in one place.
        setContents(
            sections.map((s) => ({ id: s.id, label: s.querySelector('.wql-h2').textContent.trim() }))
        );

        let frame = 0;
        const update = () => {
            frame = 0;
            const atBottom =
                window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
            let active = sections[0].id;
            if (atBottom) {
                active = sections[sections.length - 1].id;
            } else {
                for (const s of sections) {
                    if (s.getBoundingClientRect().top - ACTIVE_OFFSET <= 0) active = s.id;
                    else break;
                }
            }
            setActiveId(active);
        };
        const onScroll = () => {
            if (!frame) frame = requestAnimationFrame(update);
        };

        update();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (frame) cancelAnimationFrame(frame);
        };
    }, []);

    return (
        <React.Fragment>
            <Seo title={seo.title} description={seo.description} />
            <Navbar />
            <main className="wqf wql">
                <LegalBand title={title} />
                <div className="wqf-wrap wql-body wql-layout">
                    <nav className="wql-card wql-toc" aria-label="Contents">
                        <ol>
                            {contents.map(({ id, label }) => (
                                <li key={id}>
                                    <a
                                        href={`#${id}`}
                                        className={id === activeId ? 'is-active' : undefined}
                                        aria-current={id === activeId ? 'true' : undefined}
                                    >
                                        {label}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>
                    <article ref={docRef} className="wql-card wql-doc">
                        {children}
                    </article>
                </div>
            </main>
            <Footer />
        </React.Fragment>
    );
}

export default LegalPage;
